import React, { useEffect, useState } from 'react';
import BarChart from './BarChart';
import PieChart from './PieChart';
import LineChart from './LineChart';
import ScatterPlot from './ScatterPlot';
import ScatterBubblePlot from './ScatterBubblePlot';
import Title from '../Title';
import Legend from './Legend';
import Axes from './Axes';
import ChartScales from './ChartScales';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import { sortDataByType, sortDataByTypeOld } from '../util';

export const SUPPORTED_CHARTS = [
  'BarChart',
  'PieChart',
  'LineChart',
  'ScatterPlot',
  'ScatterBubblePlot',
];

function chartForType(chartType) {
  const chartComponent = {
    BarChart,
    PieChart,
    LineChart,
    ScatterBubblePlot,
    ScatterPlot: ScatterPlot,
  }[chartType];

  if (!chartComponent) {
    throw new Error(
      `Could not determine chart component for chart type ${chartType}`
    );
  }

  return chartComponent;
}

function sortAxesForChart(chartType, orientation) {
  switch (chartType) {
    case 'BarChart':
      return orientation === 'vertical' ? 'horizontal' : 'vertical';
    case 'LineChart':
      return 'horizontal';
    case 'PieChart':
      return 'horizontal';
    default:
      return 'horizontal';
  }
}

/**
 * @typedef ChartProps
 * @prop {*} data - Chart data
 * @prop {*} meta - Chart metadata
 * @prop {string} chartType - Type of chart
 * @prop {number} width - Chart width
 * @prop {number} height - Chart height
 * @prop {boolean} publishing - Whether chart is being published
 */
function Chart({ data, meta, chartType, width, height, publishing }) {
  const ChartComponent = chartForType(chartType);
  const [shownData, setShownData] = useState(data);
  const { get } = useReportConfiguration();
  const horizontalAxisSort = get(`horizontalAxisSort`);
  const verticalAxisSort = get(`verticalAxisSort`);
  const orientation = get('orientation');

  useEffect(() => {
    let sortAxis = 'both';

    if (sortAxesForChart(chartType, orientation) === 'vertical') {
      sortAxis = verticalAxisSort;
    } else if (sortAxesForChart(chartType, orientation) === 'horizontal') {
      sortAxis = horizontalAxisSort;
    }

    // Sort data using the utility function
    if (sortAxis && sortAxis !== 'none') {
      const sortedData = window.featureFlags
        .SUPPORT_579_better_sorting_for_pivot_chart
        ? sortDataByType(data, meta, chartType, sortAxis)
        : sortDataByTypeOld(data, meta, chartType, sortAxis);
      setShownData(sortedData);
    } else if (sortAxis === 'none') {
      setShownData(data);
    }
  }, [
    chartType,
    data,
    horizontalAxisSort,
    meta,
    orientation,
    verticalAxisSort,
  ]); // Trigger when data or horizontalAxisSort verticallAxisSort changes

  if (ChartComponent) {
    return (
      <ChartScales
        chartType={chartType}
        data={shownData}
        width={width}
        height={height}
        publishing={publishing}
      >
        <Title width={width} height={height} chartType={chartType} />
        <ChartComponent
          data={shownData}
          meta={meta}
          width={width}
          height={height}
          publishing={publishing}
        />
        <Legend
          width={width}
          height={height}
          chartType={chartType}
          publishing={publishing}
        />
        <Axes
          chartType={chartType}
          data={shownData}
          width={width}
          height={height}
        />
      </ChartScales>
    );
  } else {
    return null;
  }
}

export default Chart;
